/**
 * @file Track browser connectivity so the UI can explain a stalled write.
 *
 * navigator.onLine is only the first signal. A desktop associated to yard
 * wifi with no route out still reports true, so we confirm with a short
 * /api/health probe before treating the tab as online.
 */

import { useEffect, useState } from 'react';
import { probeReachability } from '../utils/reachability';

const PROBE_INTERVAL_MS = 20_000;

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' || navigator.onLine !== false
  );

  useEffect(() => {
    let cancelled = false;

    const markOffline = () => {
      if (!cancelled) setIsOnline(false);
    };

    const probe = async () => {
      const reachable = await probeReachability();
      if (!cancelled) setIsOnline(reachable);
    };

    const goOnline = () => {
      // Browser thinks the NIC is up — confirm we can actually reach the API.
      if (!cancelled) setIsOnline(true);
      probe();
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', markOffline);
    probe();
    const timer = setInterval(probe, PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', markOffline);
    };
  }, []);

  return isOnline;
}

export default useOnlineStatus;
